import debounce from 'debounce';
import rimg from '@pixelunion/rimg-shopify';
import AsyncView from 'shopify-asyncview';
import ScrollLock from '../utils/ScrollLock';
import layout from '../utils/layout';

export default class LiveSearch {
  constructor(section, options = {}) {
    this.el = section.querySelector('[data-search]');
    this.showBorder = options.showBorder;
    this.liveSearchEnabled = options.liveSearchEnabled;

    this.header = document.querySelector('[data-header-container]');
    this.branding = document.querySelector('[data-header-branding]');
    this.navigation = document.querySelector('[data-header-navigation]');
    this.announcement = document.querySelector('.pxs-announcement-bar');
    this.customLiquid = document.querySelector('[data-custom-liquid]');
    this.searchTriggers = section.querySelectorAll('[data-header-search-button]');
    this.searchOverlay = this.el.querySelector('[data-search-overlay]');
    this.searchForm = this.el.querySelector('[data-search-form]');
    this.searchBox = this.el.querySelector('[data-search-form-input]');
    this.closeButton = this.el.querySelector('[data-search-form-button-close]');
    this.searchResults = this.el.querySelector('[data-livesearch-results]');
    this.searchLoadingState = this.el.querySelector('[data-livesearch-loading-state]');
    this.searchQuickLinks = this.el.querySelector('[data-livesearch-quicklinks]');

    this.isTransitioning = false;

    this.stopProp = (e) => {
      e.stopPropagation();
    };

    this.open = () => {
      this.openSearchBar();
    };
    this.close = (e) => {
      e.preventDefault();
      this.closeSearchBar();
    };
    this.closeEsc = (e) => {
      if (e.key === 'Escape') {
        this._clearResults();
        this.stopProp(e);
        this.close(e);
      }
    };
    this.toggle = (e) => {
      e.preventDefault();
      this.stopProp(e);
      this.toggleSearchBar();
    };

    this.searchDebounced = debounce(this._search.bind(this), 500);
    this.resizeDebounced = debounce(this._handleResize.bind(this), 200);

    for (let i = 0; i < this.searchTriggers.length; i++) {
      this.searchTriggers[i].addEventListener('click', this.toggle);
      this.searchTriggers[i].addEventListener('focusin', this.stopProp);
    }

    this.closeButton.addEventListener('click', this.close);
    this.searchForm.addEventListener('click', this.stopProp);
    this.searchForm.addEventListener('focusin', this.stopProp);
    this.searchForm.addEventListener('submit', e => {
      if (this.searchBox.value.trim().length === 0) e.preventDefault();
    });
    this.searchResults.addEventListener('click', this.stopProp);
    this.searchResults.addEventListener('focusin', this.stopProp);
  }

  toggleSearchBar() {
    if (this.el.classList.contains('visible')) {
      this.closeSearchBar();
    } else {
      this.openSearchBar();
    }
  }

  _placeSearchBar() {
    const [leftSearch, rightSearch] = this.searchTriggers;

    // Set the visible trigger so we know which one to focus on after done searching.
    if (layout.isBreakpoint('L', 'XL')) {
      // If on desktop, the search box is shown directly below the header, but hides the nav
      // if the nav is overflowing and showing below the header.
      if (this.header.classList.contains('header-nav-overflow')
        || this.header.classList.contains('header-layout-traditional')) {
        this.navigation.classList.add('header-nav-hidden');
      }

      const announcementHeight = this.announcement ? this.announcement.getBoundingClientRect().height : 0;
      const headerHeight = this.branding.getBoundingClientRect().height;
      const customLiquidHeight = this.customLiquid ? this.customLiquid.getBoundingClientRect().height : 0;
      const borderHeight = this.showBorder ? 1 : 0;

      this.searchForm.style.top = `${announcementHeight + headerHeight + customLiquidHeight + borderHeight}px`;
      this.visibleTrigger = this.header.classList.contains('header-search-left') ? leftSearch : rightSearch;
    } else {
      // If on mobile, the search box is shown overtop of the header.
      this.searchForm.style.top = '0px';
      this.visibleTrigger = rightSearch;
    }
  }

  _handleResize() {
    this._placeSearchBar();
    this.searchResults.style.top = `${this.searchForm.getBoundingClientRect().bottom}px`;
    this._resizeSearchResults();
  }

  openSearchBar() {
    if (this.isTransitioning) {
      return;
    }

    this.isTransitioning = true;

    window.addEventListener('keydown', this.closeEsc);
    document.body.addEventListener('click', this.close);
    document.body.addEventListener('focusin', this.close);

    document.body.classList.add('search-open');

    this._placeSearchBar();

    // When placing both the form and the results div, we need to place them dynamically based on
    // the position of other elements. The results div needs to wait for the form to complete
    // animating before we can position it, thus this complicated waiting for animationend.
    this.doneSearchAnimations = () => {
      this.searchForm.classList.remove('animating-in');
      this.searchForm.classList.add('open');

      this.doneResultsAnimations = () => {
        this.searchResults.classList.remove('animating-in');
        this.searchResults.classList.add('open');
        this.searchResults.removeEventListener('animationend', this.doneResultsAnimations);
        this.isTransitioning = false;
      };

      this.searchResults.addEventListener('animationend', this.doneResultsAnimations);
      this.searchResults.classList.add('visible');
      this.searchResults.classList.add('animating-in');
      this.searchOverlay.classList.remove('animating-in');

      this.searchForm.removeEventListener('animationend', this.doneSearchAnimations);
    };

    this.searchForm.addEventListener('animationend', this.doneSearchAnimations);
    this.searchForm.classList.add('animating-in');
    this.searchOverlay.classList.add('animating-in');

    this.el.classList.add('visible');
    this.searchBox.focus();
    ScrollLock.lock(this.searchResults);

    if (this.liveSearchEnabled) {
      this.searchBox.addEventListener('keyup', this.searchDebounced);
    }

    this.resizeDebounced();

    window.addEventListener('resize', this.resizeDebounced);
  }

  closeSearchBar() {
    if (this.isTransitioning) {
      return;
    }

    this.isTransitioning = true;

    // The following animation listeners are the opposite of the animations on open, above.
    this.doneResultsAnimations = () => {
      this.searchResults.classList.remove('animating-out');
      this.searchResults.classList.remove('open');
      this.searchResults.classList.remove('visible');

      this.doneSearchAnimations = () => {
        this.searchForm.classList.remove('animating-out');
        this.searchForm.classList.remove('open');
        this.searchForm.removeEventListener('animationend', this.doneSearchAnimations);
        this.el.classList.remove('visible');
        this.searchOverlay.classList.remove('animating-out');
        document.body.classList.remove('search-open');
        this.isTransitioning = false;
      };

      this.searchForm.addEventListener('animationend', this.doneSearchAnimations);
      this.searchForm.classList.add('animating-out');

      this.searchResults.removeEventListener('animationend', this.doneResultsAnimations);
    };

    this.navigation.classList.remove('header-nav-hidden');

    this.searchResults.addEventListener('animationend', this.doneResultsAnimations);
    this.searchResults.classList.add('animating-out');
    this.searchOverlay.classList.add('animating-out');

    this.visibleTrigger.focus();
    ScrollLock.unlock();

    window.removeEventListener('keydown', this.closeEsc);
    document.body.removeEventListener('click', this.close);
    document.body.removeEventListener('focusin', this.close);

    this.searchBox.removeEventListener('keyup', this.searchDebounced);

    window.removeEventListener('resize', this.resizeDebounced);
  }

  _search() {
    const searchString = this.searchBox.value.trim();
    this.searchResults.style.top = `${this.searchForm.getBoundingClientRect().bottom}px`;

    if (this.previousSearchString === searchString) {
      return;
    }

    // Remove the predictive-search.liquid content inserted dynamically in `this._loadResults()`
    // before proceeeding. This is so that we can show the correct loading state.
    const contentEl = this.el.querySelector('[data-livesearch-content]');
    if (contentEl) contentEl.remove();

    if (searchString.length > 0) {
      this.searchResults.classList.add('livesearch-results-searching');
      this.searchResults.classList.remove('livesearch-results-empty');
      this.searchResults.classList.remove('livesearch-results-exist');
      if (this.searchQuickLinks) this.searchQuickLinks.classList.add('hidden');
      this.searchLoadingState.classList.remove('livesearch-loading-state--hidden');
      this._loadResults(searchString);
    } else {
      this.searchResults.classList.remove('livesearch-results-empty');
      this.searchResults.classList.remove('livesearch-results-exist');
      this.searchResults.classList.remove('livesearch-results-searching');
      if (this.searchQuickLinks) this.searchQuickLinks.classList.remove('hidden');
      this.searchLoadingState.classList.add('livesearch-loading-state--hidden');
    }

    this.previousSearchString = searchString;
  }

  _loadResults(searchString) {
    AsyncView.load(
      `${window.Theme.routes.predictive_search_url}?q=${searchString}&section_id=predictive-search`,
      null,
      searchString,
    ).done(({ html }) => {
      this.searchResults.style.top = `${this.searchForm.getBoundingClientRect().bottom}px`;
      this.searchResults.insertAdjacentHTML('beforeend', html);
      this._resizeSearchResults();

      // Add a timeout to give rimg a little more time to load placeholder SVG
      // to prevent images from loading at the wrong size in Firefox
      setTimeout(() => rimg.watch(this.searchResults), 1);

      this.searchResults.classList.add('livesearch-results-exist');
      this.searchResults.classList.remove('livesearch-results-searching');
      this.searchResults.classList.remove('livesearch-results-empty');
      this.searchLoadingState.classList.add('livesearch-loading-state--hidden');
    });
  }

  _resizeSearchResults() {
    const searchBoxBottomPos = this.searchBox.getBoundingClientRect().bottom;
    const offset = 30;
    const maxHeight = window.innerHeight - searchBoxBottomPos - offset;

    this.searchResults.style.setProperty('--container-max-height', `${maxHeight}px`);
  }

  _clearResults() {
    this.searchBox.value = '';

    const el = this.el.querySelector('[data-livesearch-content]');

    // This should be more performant than `innerHTMl = ""`
    while (el && el.firstChild) el.removeChild(el.firstChild);
  }

  unload() {
    this.navigation.classList.remove('header-nav-hidden');

    for (let i = 0; i < this.searchTriggers.length; i++) {
      this.searchTriggers[i].removeEventListener('click', this.toggle);
      this.searchTriggers[i].removeEventListener('focusin', this.stopProp);
    }

    this.closeButton.removeEventListener('click', this.close);
    this.searchForm.removeEventListener('click', this.stopProp);
    this.searchForm.removeEventListener('focusin', this.stopProp);
    this.searchResults.removeEventListener('click', this.stopProp);
    this.searchResults.removeEventListener('focusin', this.stopProp);

    window.removeEventListener('keydown', this.closeEsc);
    window.removeEventListener('resize', this.close);
    document.body.removeEventListener('click', this.close);
    document.body.removeEventListener('focusin', this.close);
  }
}
